<template>
  <div class="lead-appointments">
    <div class="lead-appointments__header">
      <h3 class="lead-appointments__title">
        {{ $t('lead_details.appointments') }}
        <span class="lead-appointments__count" v-if="!loading">{{ appointments.length }}</span>
      </h3>
    </div>

    <div class="lead-appointments__content">
      <LoadingSpinner v-if="loading" />

      <div v-else-if="appointments.length === 0" class="lead-appointments__empty">
        <BaseIcon name="calendar" size="48" />
        <h4>{{ $t('lead_details.no_appointments') }}</h4>
        <p>{{ $t('lead_details.no_appointments_description') }}</p>
      </div>

      <div v-else class="lead-appointments__grid">
        <div
          v-for="appointment in appointments"
          :key="appointment.id"
          class="appointment-card"
          @click="openAppointmentDetail(appointment)"
        >
          <div class="appointment-card__header">
            <div class="appointment-card__status">
              <div
                class="status-dot"
                :class="`status-dot--${appointment.status || 'pending'}`"
              ></div>
              <span class="appointment-card__status-text">
                {{ getStatusLabel(appointment.status) }}
              </span>
            </div>
            <div class="appointment-card__date">
              {{ formatDate(appointment.scheduled_at) }}
            </div>
          </div>

          <div class="appointment-card__content">
            <div class="appointment-card__title">
              <BaseIcon name="calendar" size="16" />
              <span>{{ appointment.title || $t('appointments.default_title') }}</span>
            </div>

            <div class="appointment-card__time">
              <BaseIcon name="clock" size="14" />
              <span>{{ formatTime(appointment.scheduled_at) }}</span>
              <span v-if="appointment.duration" class="appointment-card__duration">
                ({{ appointment.duration }} {{ $t('common.minutes') }})
              </span>
            </div>

            <div v-if="appointment.location" class="appointment-card__location">
              <BaseIcon name="marker" size="14" />
              <span>{{ appointment.location }}</span>
            </div>

            <div v-if="appointment.description" class="appointment-card__description">
              {{ truncateText(appointment.description, 100) }}
            </div>
          </div>

          <div class="appointment-card__footer">
            <div class="appointment-card__attendees" v-if="appointment.attendees?.length">
              <BaseIcon name="users" size="14" />
              <span>{{ appointment.attendees.length }} {{ $t('appointments.attendees') }}</span>
            </div>

            <div class="appointment-card__actions">
              <BaseIcon name="chevron" size="16" rotate="270" />
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="lead-appointments__pagination">
        <Btn
          variant="secondary"
          size="sm"
          :disabled="pagination.page <= 1"
          @click="changePage(pagination.page - 1)"
        >
          {{ $t('common.previous') }}
        </Btn>

        <span class="pagination-info">
          {{ $t('common.page_of', { current: pagination.page, total: pagination.totalPages }) }}
        </span>

        <Btn
          variant="secondary"
          size="sm"
          :disabled="pagination.page >= pagination.totalPages"
          @click="changePage(pagination.page + 1)"
        >
          {{ $t('common.next') }}
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, computed, getCurrentInstance } from 'vue';
  import { useCrmStore } from '@/store/crmStore';
  import { useUIStore } from '@/store/uiStore';
  import { formatDate } from '@/utils/date';

  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default defineComponent({
    name: 'LeadAppointments',

    components: {
      LoadingSpinner,
      BaseIcon,
      Btn,
    },

    props: {
      leadId: {
        type: String,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const crmStore = useCrmStore();
      const uiStore = useUIStore();

      const loading = computed(() => crmStore.leadAppointmentsLoading);
      const appointments = computed(() => crmStore.leadAppointments);
      const pagination = computed(() => crmStore.leadAppointmentsPagination);

      const getStatusLabel = (status) => {
        const statusLabels = {
          pending: $t('appointments.statuses.pending'),
          confirmed: $t('appointments.statuses.confirmed'),
          booked: $t('appointments.statuses.booked'),
          cancelled: $t('appointments.statuses.cancelled'),
          completed: $t('appointments.statuses.completed'),
        };
        return statusLabels[status] || $t('appointments.statuses.pending');
      };

      const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
        });
      };

      const truncateText = (text, maxLength) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
      };

      const openAppointmentDetail = (appointment) => {
        // TODO: Implement appointment detail modal/page
        console.log('Open appointment detail:', appointment);
      };

      const changePage = async (page) => {
        if (page >= 1 && page <= pagination.value.totalPages) {
          await crmStore.fetchLeadAppointments(props.leadId, {
            page,
            limit: pagination.value.limit,
          });
        }
      };

      const loadAppointments = async () => {
        try {
          await crmStore.fetchLeadAppointments(props.leadId, { page: 1, limit: 20 });
        } catch (error) {
          console.error('Failed to load lead appointments:', error);
        }
      };

      onMounted(() => {
        loadAppointments();
      });

      return {
        loading,
        appointments,
        pagination,
        formatDate,
        formatTime,
        getStatusLabel,
        truncateText,
        openAppointmentDetail,
        changePage,
      };
    },
  });
</script>

<style scoped lang="scss">
  .lead-appointments {
    &__header {
      margin-bottom: 1.5rem;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
    }

    &__count {
      background: var(--slate-100);
      color: var(--slate-600);
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
    }

    &__empty {
      text-align: center;
      padding: 3rem 1rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-300);
        margin-bottom: 1rem;
      }

      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        font-weight: 600;
      }

      p {
        margin: 0;
        font-size: 0.875rem;
      }
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 1rem;

      @include md-down {
        grid-template-columns: 1fr;
      }
    }

    &__pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      margin-top: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--slate-200);

      .pagination-info {
        font-size: 0.875rem;
        color: var(--slate-600);
      }
    }
  }

  .appointment-card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;
    padding: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--brand-300);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    &__status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__status-text {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }

    &__date {
      font-size: 0.75rem;
      color: var(--slate-500);
      font-weight: 500;
    }

    &__content {
      margin-bottom: 1rem;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: var(--slate-900);
      margin-bottom: 0.75rem;

      .icon {
        color: var(--brand-500);
      }
    }

    &__time {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-600);
      margin-bottom: 0.5rem;

      .icon {
        color: var(--slate-400);
      }
    }

    &__duration {
      color: var(--slate-500);
      font-size: 0.8125rem;
    }

    &__location {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-600);
      margin-bottom: 0.5rem;

      .icon {
        color: var(--slate-400);
      }
    }

    &__description {
      font-size: 0.875rem;
      color: var(--slate-600);
      line-height: 1.4;
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);
    }

    &__attendees {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-600);

      .icon {
        color: var(--slate-400);
      }
    }

    &__actions {
      .icon {
        color: var(--slate-400);
      }
    }
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &--pending {
      background-color: var(--amber-500);
    }

    &--confirmed {
      background-color: var(--blue-500);
    }

    &--booked {
      background-color: var(--teal-500);
    }

    &--cancelled {
      background-color: var(--red-500);
    }

    &--completed {
      background-color: var(--green-500);
    }
  }
</style>
